import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi, getFieldViolations } from '@/api/errors';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { formatDateTime } from '@/lib/format';

import type { ConfigItemViewModel } from '../api';
import { SourceBadge } from './SourceBadge';

interface ConfigDetailSheetProps {
  readonly config: ConfigItemViewModel | null;
  /** ENUM allowed values, sourced from the catalog definition for this key. */
  readonly enumValues: readonly string[] | null;
  /**
   * Whether the current user holds `admin:MANAGE`. A sensitive key (secret /
   * security / password) is editable only when this is true; otherwise — per the
   * "hide, do not disable" rule (PTV2-915) — the edit affordance is hidden and the
   * value shown read-only.
   */
  readonly canEditSensitive: boolean;
  readonly onClose: () => void;
  readonly onSave: (input: { value: string; reason?: string }) => void;
  readonly onReset: () => void;
  readonly savePending: boolean;
  readonly resetPending: boolean;
  readonly saveError: unknown;
  readonly resetError: unknown;
}

export function ConfigDetailSheet({
  config,
  enumValues,
  canEditSensitive,
  onClose,
  onSave,
  onReset,
  savePending,
  resetPending,
  saveError,
  resetError,
}: ConfigDetailSheetProps): ReactElement {
  const { t } = useTranslation();
  const open = config !== null;

  const initialValue = config?.value ?? '';
  const [draft, setDraft] = useState<string>(initialValue);
  const [reason, setReason] = useState<string>('');
  const [confirmingReset, setConfirmingReset] = useState<boolean>(false);

  // Re-seed the editor only when a *different* key is opened — not on every
  // value change of the same key. A background list refetch (refetch-on-focus,
  // or the post-save invalidate) can mutate `config.value` while the admin is
  // mid-edit; re-seeding on that would silently discard their typed input.
  const seededKey = useRef<string | null>(null);
  useEffect(() => {
    const key = config?.key ?? null;
    if (key !== seededKey.current) {
      seededKey.current = key;
      setDraft(config?.value ?? '');
      setReason('');
      setConfirmingReset(false);
    }
  }, [config?.key, config?.value]);

  const fieldError = useMemo(() => {
    const violations = getFieldViolations(saveError);
    if (violations.length > 0) return violations[0]?.message ?? null;
    return null;
  }, [saveError]);

  // Closed state: render no SheetContent at all so Radix never mounts a
  // Content node without a Title/Description (avoids dev a11y warnings).
  if (config === null) {
    return <Sheet open={false} onOpenChange={onClose} />;
  }

  const isSecret = config.secret;
  // Sensitive = secret OR security/password-tagged (the keys the backend gates on admin:MANAGE).
  const sensitive = isSecret || config.tags.includes('security') || config.tags.includes('password');
  const editable = !sensitive || canEditSensitive;
  const dirty = draft !== initialValue;
  const saveDisabled = isSecret
    ? draft.length === 0 || savePending
    : !dirty || draft.length === 0 || savePending;
  const description =
    config.description !== null && config.description !== '' ? config.description : null;
  const modifiedOn = config.modifiedOn !== null ? formatDateTime(config.modifiedOn) : null;

  function handleSave(): void {
    onSave({ value: draft, ...(reason !== '' ? { reason } : {}) });
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="break-all font-mono text-base">{config.key}</SheetTitle>
          {/* Always render a Description so Radix has an accessible description
              (avoids dev a11y warnings); fall back to a visually-hidden generic
              label when the key carries no description text. */}
          {description !== null ? (
            <SheetDescription>{description}</SheetDescription>
          ) : (
            <SheetDescription className="sr-only">
              {t('routes.admin.configs.detail.descriptionFallback', { key: config.key })}
            </SheetDescription>
          )}
        </SheetHeader>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          <Meta label={t('routes.admin.configs.detail.type')}>
            <span className="font-mono text-xs">{config.type}</span>
          </Meta>
          <Meta label={t('routes.admin.configs.detail.scope')}>
            <span className="font-mono text-xs">{config.scope}</span>
          </Meta>
          <Meta label={t('routes.admin.configs.detail.exposure')}>
            {/* AUTHENTICATED = the value is user-readable via GET /features —
                make widened exposure visually loud so it's a deliberate state. */}
            <Badge variant={config.exposure === 'AUTHENTICATED' ? 'default' : 'outline'}>
              {config.exposure}
            </Badge>
          </Meta>
          <Meta label={t('routes.admin.configs.detail.source')}>
            <SourceBadge source={config.source} />
          </Meta>
          <Meta label={t('routes.admin.configs.detail.default')}>
            <span className="break-all font-mono text-xs text-muted-foreground">
              {isSecret
                ? t('routes.admin.configs.value.secret')
                : (config.defaultValue ?? t('routes.admin.configs.value.unset'))}
            </span>
          </Meta>
          {config.tags.length > 0 && (
            <Meta label={t('routes.admin.configs.detail.tags')} className="col-span-2">
              <div className="flex flex-wrap gap-1">
                {config.tags.map((tag) => (
                  <Badge key={tag} variant="outline">
                    {tag}
                  </Badge>
                ))}
              </div>
            </Meta>
          )}
          {modifiedOn !== null && (
            <Meta label={t('routes.admin.configs.detail.modified')} className="col-span-2">
              <span className="text-xs text-muted-foreground">
                {config.modifiedBy !== null && config.modifiedBy !== ''
                  ? t('routes.admin.configs.detail.modifiedBy', {
                      date: modifiedOn,
                      user: config.modifiedBy,
                    })
                  : modifiedOn}
              </span>
            </Meta>
          )}
        </dl>

        {!editable && (
          <div className="space-y-1.5">
            <span className="block text-sm font-medium">
              {t('routes.admin.configs.detail.valueLabel')}
            </span>
            <p className="break-all font-mono text-sm text-muted-foreground">
              {isSecret
                ? t('routes.admin.configs.value.secret')
                : (config.value ?? t('routes.admin.configs.value.unset'))}
            </p>
            <p className="text-xs text-muted-foreground">
              {t('routes.admin.configs.detail.managedReadOnly')}
            </p>
          </div>
        )}

        {editable && (
          <>
        <div className="space-y-2">
          <label htmlFor="config-value-editor" className="block text-sm font-medium">
            {t('routes.admin.configs.detail.valueLabel')}
          </label>
          <ValueEditor
            type={config.type}
            enumValues={enumValues}
            secret={isSecret}
            value={draft}
            onChange={setDraft}
            invalid={fieldError !== null}
          />
          {config.type === 'DURATION' && (
            <p className="text-xs text-muted-foreground">
              {t('routes.admin.configs.detail.durationHint')}
            </p>
          )}
          {fieldError !== null && (
            <p role="alert" className="text-xs text-red-600">
              {fieldError}
            </p>
          )}
          {saveError !== undefined && saveError !== null && fieldError === null && (
            <p role="alert" className="text-xs text-red-600">
              {t('routes.admin.configs.detail.saveError', {
                code: getErrorCodeForUi(saveError),
              })}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <label htmlFor="config-reason" className="block text-sm font-medium">
            {t('routes.admin.configs.detail.reasonLabel')}
          </label>
          <Input
            id="config-reason"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
            placeholder={t('routes.admin.configs.detail.reasonPlaceholder')}
            autoComplete="off"
          />
        </div>

        {resetError !== undefined && resetError !== null && (
          <p role="alert" className="text-xs text-red-600">
            {t('routes.admin.configs.detail.resetError', {
              code: getErrorCodeForUi(resetError),
            })}
          </p>
        )}

        <SheetFooter>
          {confirmingReset ? (
            <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-xs text-muted-foreground">
                {t('routes.admin.configs.detail.resetConfirm')}
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setConfirmingReset(false);
                  }}
                  disabled={resetPending}
                >
                  {t('routes.admin.configs.detail.cancel')}
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  loading={resetPending}
                  onClick={onReset}
                >
                  {t('routes.admin.configs.detail.resetConfirmAction')}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setConfirmingReset(true);
                }}
                disabled={!config.isOverridden || savePending || resetPending}
              >
                {t('routes.admin.configs.detail.reset')}
              </Button>
              <Button type="button" loading={savePending} disabled={saveDisabled} onClick={handleSave}>
                {t('routes.admin.configs.detail.save')}
              </Button>
            </>
          )}
        </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Meta({
  label,
  className,
  children,
}: {
  readonly label: string;
  readonly className?: string;
  readonly children: ReactElement | string;
}): ReactElement {
  return (
    <div className={className}>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}

interface ValueEditorProps {
  readonly type: string;
  readonly enumValues: readonly string[] | null;
  readonly secret: boolean;
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly invalid: boolean;
}

/**
 * Type-aware editor. Values are edited in their canonical-string form — the same
 * shape the API stores and `PUT` accepts — so there is no client-side parse.
 * BOOL uses a true/false Select (no Switch primitive ships in Phase 3 and no
 * BOOL key ships either); secrets render a write-only password input.
 */
function ValueEditor({
  type,
  enumValues,
  secret,
  value,
  onChange,
  invalid,
}: ValueEditorProps): ReactElement {
  const { t } = useTranslation();
  const [reveal, setReveal] = useState<boolean>(false);

  if (secret) {
    // Write-only: the field is always seeded empty (the cleartext is never sent to the client),
    // so this is a "Set / Replace" input. A show/hide toggle helps the operator check what they
    // typed before saving.
    return (
      <div className="flex gap-2">
        <Input
          id="config-value-editor"
          type={reveal ? 'text' : 'password'}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          placeholder={t('routes.admin.configs.detail.secretPlaceholder')}
          autoComplete="new-password"
          aria-invalid={invalid}
          className="flex-1"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            setReveal((r) => !r);
          }}
          aria-label={reveal
            ? t('routes.admin.configs.detail.hideSecret')
            : t('routes.admin.configs.detail.showSecret')}
        >
          {reveal
            ? t('routes.admin.configs.detail.hideSecret')
            : t('routes.admin.configs.detail.showSecret')}
        </Button>
      </div>
    );
  }

  if (type === 'ENUM') {
    return (
      <Select
        id="config-value-editor"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        aria-invalid={invalid}
      >
        <option value="">{t('routes.admin.configs.detail.enumPlaceholder')}</option>
        {(enumValues ?? []).map((v) => (
          <option key={v} value={v}>
            {v}
          </option>
        ))}
      </Select>
    );
  }

  if (type === 'BOOL') {
    return (
      <Select
        id="config-value-editor"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        aria-invalid={invalid}
      >
        <option value="">{t('routes.admin.configs.detail.boolPlaceholder')}</option>
        <option value="true">{t('routes.admin.configs.detail.boolTrue')}</option>
        <option value="false">{t('routes.admin.configs.detail.boolFalse')}</option>
      </Select>
    );
  }

  if (type === 'INT' || type === 'LONG') {
    return (
      <Input
        id="config-value-editor"
        type="number"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        className="font-mono text-sm"
        aria-invalid={invalid}
      />
    );
  }

  // STRING, DURATION, URL → free-text canonical string.
  return (
    <Input
      id="config-value-editor"
      type="text"
      value={value}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      spellCheck={false}
      className="font-mono text-sm"
      aria-invalid={invalid}
    />
  );
}
